import React from 'react';
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { PopularPostList } from 'organisms';
import useStyles from './styles';
import { AdSpace } from 'molecules';
import { SocialMediaGroup } from 'molecules';
import { PageProps } from 'types/page';
import { SOCIAL_MEDIA } from 'config/Nav';
import {
  SectionHeader,
  SocialMediaLink,
  SocialMediaType,
  SizeVariant,
} from '@elevator/ui';

/**
 * Sidebar Component
 *
 * Layout component that wraps page content with a sidebar containing social media links,
 * ad spaces, and trending posts. Supports two layout modes:
 * - 'side-by-side': Content on left (7 cols), sidebar on right (5 cols) - posts use 'small' variant (single column)
 * - 'full-width': Content full-width, sidebar below - posts use 'main' variant (grid)
 *
 * Features:
 * - Responsive layout (stacks on mobile)
 * - Social media links section
 * - Ad space integration
 * - Trending posts list (optional)
 * - Configurable layout modes
 * - Configurable social media icon sizes
 *
 * Used in:
 * - Page layout (e.g. contact, how-to-report-scammers)
 *
 * @param props - Component props
 * @param props.children - Main content to display alongside sidebar
 * @param props.layout - Layout mode: 'side-by-side' (default) or 'full-width'
 * @param props.showTrendingPosts - Whether to show trending posts section. Defaults to true.
 * @param props.showSidebarAd - Whether to show the sidebar banner ad. Defaults to true.
 * @param props.socialMediaVariant - Size variant for social media icons. Defaults to 'medium'.
 * @returns Sidebar layout with content and sidebar sections
 */
const Sidebar = (props: SidebarProps): React.JSX.Element => {
  const { classes } = useStyles();
  const theme = useTheme();
  const {
    children,
    layout = 'side-by-side',
    showTrendingPosts = true,
    socialMediaVariant = 'medium',
    showSidebarAd = true,
  } = props;

  const getElevatorMediaSocials = (): SocialMediaLink[] => {
    return Object.values(SOCIAL_MEDIA).map((social) => {
      const type = social.type as SocialMediaType;
      return {
        type,
        link: social.route,
      };
    });
  };

  const renderSidebarContent = (): React.JSX.Element => {
    return (
      <Box component="aside" className={classes.asideContainer}>
        <Stack spacing={3} width="100%">
          <Stack spacing={1} width="100%">
            <SectionHeader title={'Connect'} size={'large'} />
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              paddingTop={3}
            >
              <SocialMediaGroup
                variant={socialMediaVariant}
                socialMedia={getElevatorMediaSocials()}
              />
            </Box>
          </Stack>
          {showSidebarAd ? (
            <Box display="flex" justifyContent="center" width="100%">
              <AdSpace
                type={'banner-ad'}
                adUnit={'elevator-sidebar-halfpage1'}
                size={'md'}
              />
            </Box>
          ) : null}
          {showTrendingPosts && <PopularPostList />}
        </Stack>
      </Box>
    );
  };

  const grid = (
    <Grid2
      container
      spacing={0}
      sx={{
        rowGap: { xs: theme.spacing(3), md: 0 },
      }}
    >
      <Grid2
        size={layout === 'full-width' ? 12 : { xs: 12, md: 7 }}
        className={classes.postContainer}
      >
        {children}
      </Grid2>
      <Grid2
        size={layout === 'full-width' ? 12 : { xs: 12, md: 5 }}
        className={
          layout === 'full-width' ? classes.fullWidthSidebarContainer : undefined
        }
      >
        {renderSidebarContent()}
      </Grid2>
    </Grid2>
  );

  return (
    <Box className={classes.container} data-layout="sidebar">
      {grid}
    </Box>
  );
};

export interface SidebarProps extends PageProps {
  /** Main content to display alongside sidebar */
  children: React.ReactNode;
  /**
   * Layout mode for the sidebar.
   * - 'side-by-side': Content on left (7 cols), sidebar on right (5 cols) - posts use 'small' variant (single column)
   * - 'full-width': Content full-width, sidebar below - posts use 'main' variant (grid)
   * @default 'side-by-side'
   */
  layout?: 'side-by-side' | 'full-width';
  /** Whether to show the trending posts section. Defaults to true. */
  showTrendingPosts?: boolean;
  /** Whether to show the sidebar banner ad. Defaults to true. */
  showSidebarAd?: boolean;
  /** Size variant for social media icons. Defaults to 'medium'. */
  socialMediaVariant?: SizeVariant;
}

export default Sidebar;
